/* eslint-disable */

import React, { useState } from 'react'
import { Paper, Grid, TextField, Box, IconButton, Button, Tooltip, ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RefreshIcon from '@mui/icons-material/Refresh';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import BrushIcon from '@mui/icons-material/Brush';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

interface ColorChartHeaderProps {
  name: string | null;
  description: string | null;
  canUndo: boolean;
  canRedo: boolean;
  tool: 'brush' | 'bucket' | 'text';
  textInput: string;
  brushSize: number;
  fontStyle: 'standard' | 'bold' | 'cursive' | 'narrow' | 'wide';
  textAlignment: 'left' | 'center' | 'right';
  craft: 'knitting' | 'crochet' | 'cross_stitch' | null;
  crochetStitchType: 'sc' | 'hdc' | 'dc' | null;
  isEditMode: boolean;
  showCraftButton?: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onReset?: () => void;
  onToolChange: (tool: 'brush' | 'bucket' | 'text') => void;
  onTextInputChange: (text: string) => void;
  onBrushSizeChange: (size: number) => void;
  onFontStyleChange: (style: 'standard' | 'bold' | 'cursive' | 'narrow' | 'wide') => void;
  onTextAlignmentChange: (alignment: 'left' | 'center' | 'right') => void;
  onCraftChange: (craft: 'knitting' | 'crochet' | 'cross_stitch') => void;
  onCrochetStitchTypeChange: (stitchType: 'sc' | 'hdc' | 'dc') => void;
}

export default function ColorChartHeader({
  name,
  description,
  canUndo,
  canRedo,
  tool,
  textInput,
  brushSize,
  fontStyle,
  textAlignment,
  craft,
  crochetStitchType,
  isEditMode,
  showCraftButton = true,
  onNameChange,
  onDescriptionChange,
  onUndo,
  onRedo,
  onSave,
  onReset,
  onToolChange,
  onTextInputChange,
  onBrushSizeChange,
  onFontStyleChange,
  onTextAlignmentChange,
  onCraftChange,
  onCrochetStitchTypeChange
}: ColorChartHeaderProps) {
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [craftModalOpen, setCraftModalOpen] = useState(false);
  const [stitchModalOpen, setStitchModalOpen] = useState(false);

  const handleOpenTextModal = () => {
    setTextModalOpen(true);
  };

  const handleCloseTextModal = () => {
    setTextModalOpen(false);
  };

  const handleOpenCraftModal = () => {
    setCraftModalOpen(true);
  };

  const handleCloseCraftModal = () => {
    setCraftModalOpen(false);
  };

  const handleCraftSelect = (selectedCraft: 'knitting' | 'crochet' | 'cross_stitch') => {
    onCraftChange(selectedCraft);
    setCraftModalOpen(false);
    if (selectedCraft === 'crochet' && !crochetStitchType) {
      setStitchModalOpen(true);
    }
  };

  const handleOpenStitchModal = () => {
    if (craft === 'crochet') {
      setStitchModalOpen(true);
    }
  };

  const handleCloseStitchModal = () => {
    setStitchModalOpen(false);
  };

  const handleStitchSelect = (selectedStitch: 'sc' | 'hdc' | 'dc') => {
    onCrochetStitchTypeChange(selectedStitch);
    setStitchModalOpen(false);
  };

  const getCraftLabel = () => {
    if (!craft) return 'Craft';
    
    const craftLabels: Record<string, string> = {
      knitting: 'Knitting',
      crochet: 'Crochet',
      cross_stitch: 'Cross Stitch'
    }
    
    const stitchLabels: Record<string, string> = {
      sc: 'Single Crochet',
      hdc: 'Half Double',
      dc: 'Double'
    }
    
    if (craft === 'crochet' && crochetStitchType) {
      return `${stitchLabels[crochetStitchType]}`
    }
    
    return craftLabels[craft] || 'Craft';
  };

  return (
    <Paper elevation={2} sx={{ padding: 1.5, mb: 1.5 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
            {onReset && (
              <Tooltip title="Reset">
                <IconButton
                  onClick={onReset}
                  color="warning"
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <TextField
              fullWidth
              size="small"
              label="Color Chart Name"
              variant="outlined"
              value={name || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onNameChange(event.target.value);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            multiline
            label="Description"
            variant="outlined"
            rows={1}
            value={description || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onDescriptionChange(event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%', justifyContent: 'flex-start' }}>
            <ToggleButtonGroup
              value={tool}
              exclusive
              onChange={(e: React.MouseEvent<HTMLElement>, val: 'brush' | 'bucket' | 'text' | null) => val && onToolChange(val)}
              size="small"
            >
              <Tooltip title="Brush Tool">
                <ToggleButton value="brush" sx={{ px: 1.5 }}>
                  <BrushIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Fill Tool">
                <ToggleButton value="bucket" sx={{ px: 1.5 }}>
                  <FormatColorFillIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Text Tool">
                <ToggleButton value="text" sx={{ px: 1.5 }}>
                  <TextFieldsIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            {tool === 'text' && (
              <Button
                onClick={handleOpenTextModal}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ px: 1, py: 0.5, fontSize: '0.875rem' }}
              >
                Edit Text
              </Button>
            )}
            {tool === 'brush' && (
              <ToggleButtonGroup
                value={brushSize}
                exclusive
                onChange={(e: React.MouseEvent<HTMLElement>, val: number | null) => val !== null && onBrushSizeChange(val)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.5, fontSize: '0.875rem' } }}
              >
                <ToggleButton value={1}>1×1</ToggleButton>
                <ToggleButton value={2}>2×2</ToggleButton>
                <ToggleButton value={3}>3×3</ToggleButton>
                <ToggleButton value={5}>5×5</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
            {isEditMode && showCraftButton && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenCraftModal}
                sx={{ px: 1.5, py: 0.5, fontSize: '0.875rem' }}
              >
                {getCraftLabel()}
              </Button>
            )}
            <Tooltip title="Undo (Ctrl+Z)">
              <span>
                <IconButton
                  onClick={onUndo}
                  disabled={!canUndo}
                  color="primary"
                  size="small"
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z or Ctrl+Y)">
              <span>
                <IconButton
                  onClick={onRedo}
                  disabled={!canRedo}
                  color="primary"
                  size="small"
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={onSave}
              size="small"
              sx={{ px: 2, py: 0.75, fontSize: '0.875rem' }}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Text Input Modal */}
      <Dialog
        open={textModalOpen}
        onClose={handleCloseTextModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Text</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Text"
              placeholder="Enter text to place on chart"
              value={textInput}
              onChange={(e) => onTextInputChange(e.target.value)}
              autoFocus
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: textAlignment,
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ToggleButtonGroup
                value={textAlignment}
                exclusive
                onChange={(e: React.MouseEvent<HTMLElement>, val: 'left' | 'center' | 'right' | null) => val && onTextAlignmentChange(val)}
                aria-label="text alignment"
              >
                <Tooltip title="Left Align">
                  <ToggleButton value="left" aria-label="left aligned">
                    <FormatAlignLeftIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Center Align">
                  <ToggleButton value="center" aria-label="centered">
                    <FormatAlignCenterIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Right Align">
                  <ToggleButton value="right" aria-label="right aligned">
                    <FormatAlignRightIcon />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTextModal}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Craft Selection Modal */}
      {craftModalOpen && (
        <Dialog
          open={craftModalOpen}
          onClose={handleCloseCraftModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Craft</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Button
                variant={craft === 'knitting' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('knitting')}
                sx={{ py: 2 }}
              >
                Knitting
              </Button>
              <Button
                variant={craft === 'crochet' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('crochet')}
                sx={{ py: 2 }}
              >
                Crochet
              </Button>
              <Button
                variant={craft === 'cross_stitch' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('cross_stitch')}
                sx={{ py: 2 }}
              >
                Cross Stitch
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCraftModal}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Crochet Stitch Type Modal */}
      {stitchModalOpen && (
        <Dialog
          open={stitchModalOpen}
          onClose={handleCloseStitchModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Stitch Type</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Button
                variant={crochetStitchType === 'sc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('sc')}
                sx={{ py: 2 }}
              >
                Single Crochet
              </Button>
              <Button
                variant={crochetStitchType === 'hdc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('hdc')}
                sx={{ py: 2 }}
              >
                Half Double Crochet
              </Button>
              <Button
                variant={crochetStitchType === 'dc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('dc')}
                sx={{ py: 2 }}
              >
                Double Crochet
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStitchModal}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  )
}

